import { Routes, Route, Navigate } from 'react-router-dom';
import { Capacitor } from '@capacitor/core';
import LandingPage from './pages/LandingPage';
import HomePage from './pages/HomePage';
import AnglePage from './pages/AnglePage';
import HeightPage from './pages/HeightPage';
import TorsionPage from './pages/TorsionPage';
import AccountSettingsPage from './pages/AccountSettingsPage';
import {
  PrivacyPage,
  TermsPage,
  ImpressumPage,
  DeleteAccountPage,
} from './pages/LegalPage';
import { RequireSession } from './components/RequireSession';
import { RequirePro } from './components/RequirePro';

/* Native shell (Capacitor) detection.
 *
 * iOS Capacitor serves the bundle from `capacitor://localhost`,
 * Android Capacitor serves it from `http://localhost` (plain
 * HTTP scheme). A naïve protocol check therefore misses Android
 * entirely — the user would land on the marketing LandingPage
 * inside the app, which is dead weight (they already installed it).
 *
 * `Capacitor.isNativePlatform()` returns true on BOTH iOS and
 * Android shells, and false on web (PWA / Safari). That's the
 * boundary we actually care about. */
const isNativeShell =
  typeof window !== 'undefined' && Capacitor.isNativePlatform();

export default function App() {
  return (
    <Routes>
      {/* `/` is marketing on the web, but lands directly on the measurement
          surface inside the native iOS app. */}
      <Route
        path="/"
        element={isNativeShell ? <Navigate to="/app" replace /> : <LandingPage />}
      />
      {/* Legal pages stay public on both surfaces — Apple's App Review
          checks for reachable Privacy and Terms from inside the app. */}
      <Route path="/privacy" element={<PrivacyPage />} />
      <Route path="/terms" element={<TermsPage />} />
      <Route path="/impressum" element={<ImpressumPage />} />
      {/* Public account-deletion page — required by Google Play Console
          (Data Safety → Account Deletion URL). Apple does not require
          this but having it doesn't hurt. The actual in-app deletion
          flow lives in AccountSettingsPage; this page just documents
          both the in-app path and the e-mail-based path. */}
      <Route path="/delete-account" element={<DeleteAccountPage />} />

      {/* App lives under /app/* — sign-in required. RequireSession
          renders a brand-aligned gate when the user isn't authenticated
          (or hasn't verified their email yet) and bounces through
          plan.cadtomie.com for the actual sign-in form. */}
      <Route
        path="/app"
        element={
          <RequireSession>
            <HomePage />
          </RequireSession>
        }
      />
      {/* Measurement modules — sign-in AND Pro subscription required.
          Pro is the unforgeable boundary between Educational (free)
          and the full measurement workflow. Apple Review needs to see
          that paid features are actually paid; without this gate the
          subscription is decorative. */}
      <Route
        path="/app/angle"
        element={
          <RequireSession>
            <RequirePro>
              <AnglePage />
            </RequirePro>
          </RequireSession>
        }
      />
      <Route
        path="/app/height"
        element={
          <RequireSession>
            <RequirePro>
              <HeightPage />
            </RequirePro>
          </RequireSession>
        }
      />
      <Route
        path="/app/torsion"
        element={
          <RequireSession>
            <RequirePro>
              <TorsionPage />
            </RequirePro>
          </RequireSession>
        }
      />
      {/* Account settings — sign-in required. Houses sign-out, password &
          email management (deep-link to planner), and account deletion. */}
      <Route
        path="/app/account"
        element={
          <RequireSession>
            <AccountSettingsPage />
          </RequireSession>
        }
      />

      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
